import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DataTable, columnTypes as colType, innings, percent, per100IP, normalize } from 'yankees-react-utils';

const StyledSuperheaderRow = styled.tr`
    > td {
        font-weight: bold;
        text-align: center;
    }
`;
const StyledDataTable = styled(DataTable)`
    tbody tr td.name {
        width: 20%;
        text-align: left;
    }
`;

const catcherColumns = (translate) => [
    { id: 'name', cellClass: 'cell-10', title: translate('baseball.player'), type: colType.TEXT, transform: (row) => row.name },
    { id: 'inn', title: translate('baseball.abbr-inning'), type: colType.TEXT, transform: (row) => row.inn },
    { id: 'sb', title: translate('baseball.abbr-stolen-base'), type: colType.NUMBER, transform: (row) => row.sb },
    { id: 'cs', title: translate('baseball.abbr-caught-stealing'), type: colType.NUMBER, transform: (row) => row.cs },
    { id: 'sbperc', title: `${translate('baseball.abbr-stolen-base')}%`, type: colType.TEXT, transform: (row) => row.sbperc },
    { id: 'sbper100', title: `${translate('baseball.abbr-stolen-base')}/100 ${translate('baseball.abbr-inning')}`, type: colType.TEXT, transform: (row) => row.sbper100 },
    { id: 'pb', title: translate('sections.opponent.team.abbr-passed-ball'), type: colType.NUMBER, transform: (row) => row.pb },
    { id: 'sbperc2b', title: `${translate('baseball.abbr-stolen-base')}% ${translate('baseball.2b')}`, type: colType.TEXT, transform: (row) => row.sbperc2b },
    { id: 'sbperc3b', title: `${translate('baseball.abbr-stolen-base')}% ${translate('baseball.3b')}`, type: colType.TEXT, transform: (row) => row.sbperc3b },
    { id: 'pkoperatt', title: `${translate('sections.opponent.team.abbr-pko')}/Att`, type: colType.TEXT, transform: (row) => row.pkoperatt },
    { id: 'time2b', title: `${translate('baseball.2b')} ${translate('sections.opponent.team.time')}`, type: colType.TEXT, transform: (row) => row.time2b },
    { id: 'range2b', title: `${translate('baseball.2b')} ${translate('sections.opponent.team.range')}`, type: colType.TEXT, transform: (row) => row.range2b },
    { id: 'throwaccperc', title: `${translate('sections.opponent.team.throw')} ${translate('sections.opponent.team.abbr-accuracy')} %`, type: colType.TEXT, transform: (row) => row.throwaccperc },
    { id: 'throw1stand3rd', title: `${translate('sections.opponent.team.throw')} 1st & 3rd`, type: colType.TEXT, transform: (row) => row.throw1stand3rd },
];

const formatCatcherRows = (translate, catcher = [], leagueaverage = [], currentYear) => {
    const currentYearRows = [];
    const twoYearRows = [];
    let maxOuts1Yr = 0;
    let maxOuts4Yr = 0;
    catcher.forEach((c) => {
        maxOuts1Yr = c.outs_1yr > maxOuts1Yr ? c.outs_1yr : maxOuts1Yr;
        const catcherRowCurrentYear = {
            year: currentYear,
            name: c.catcher,
            inn: innings(c.outs_1yr),
            sb: c.sb_1yr ? c.sb_1yr : '--',
            cs: c.ccs_1yr ? c.ccs_1yr : '--',
            sbperc: c.sb_1yr && (c.ccs_1yr || c.sb_1yr) ? percent(c.sb_1yr / (c.ccs_1yr + c.sb_1yr)) : '--',
            sbper100: c.sb_1yr && c.outs_1yr ? per100IP(c.sb_1yr, c.outs_1yr).toFixed(1) : '--',
            pb: c.pb_1yr ? c.pb_1yr : '--',
            sbperc2b: c.sb2b_1yr && (c.sb2b_1yr || c.ccs2b_1yr) ? percent(c.sb2b_1yr / (c.sb2b_1yr + c.ccs2b_1yr)) : '--',
            sbperc3b: c.sb3b_1yr && (c.sb3b_1yr || c.ccs3b_1yr) ? percent(c.sb3b_1yr / (c.sb3b_1yr + c.ccs3b_1yr)) : '--',
            pkoperatt: `${c.pko_1yr}/${c.pkoatt_1yr}`,
            time2b: c.time2b_1yr?.toFixed(2),
            range2b: c.time2b10pct_1yr && c.time2b90pct_1yr ? `${c.time2b10pct_1yr.toFixed(2)}-${c.time2b90pct_1yr.toFixed(2)}` : '--',
            throwaccperc: c.goodthrows2b_1yr && c.throws2bwloc_1yr ? percent(c.goodthrows2b_1yr / c.throws2bwloc_1yr) : '--',
            throw1stand3rd: `${c.firstandthirdthrows_1yr}/${c.firstandthirdopps_1yr} (${c.firstandthirdopps_1yr ? percent(c.firstandthirdthrows_1yr / c.firstandthirdopps_1yr) : '--'})`,
        };
        currentYearRows.push(catcherRowCurrentYear);

        maxOuts4Yr = c.outs_4yr > maxOuts4Yr ? c.outs_4yr : maxOuts4Yr;
        const catcherRowTwoYear = {
            year: -1,
            name: c.catcher,
            inn: innings(c.outs_4yr),
            sb: c.sb_4yr ? c.sb_4yr : '--',
            cs: c.ccs_4yr ? c.ccs_4yr : '--',
            sbperc: c.sb_4yr && (c.ccs_4yr || c.sb_4yr) ? percent(c.sb_4yr / (c.ccs_4yr + c.sb_4yr)) : '--',
            sbper100: c.sb_4yr && c.outs_4yr ? per100IP(c.sb_4yr, c.outs_4yr).toFixed(1) : '--',
            pb: c.pb_4yr ? c.pb_4yr : '--',
            sbperc2b: c.sb2b_4yr && (c.sb2b_4yr || c.ccs2b_4yr) ? percent(c.sb2b_4yr / (c.sb2b_4yr + c.ccs2b_4yr)) : '--',
            sbperc3b: c.sb3b_4yr && (c.sb3b_4yr || c.ccs3b_4yr) ? percent(c.sb3b_4yr / (c.sb3b_4yr + c.ccs3b_4yr)) : '--',
            pkoperatt: `${c.pko_4yr}/${c.pkoatt_4yr}`,
            time2b: c.time2b_4yr?.toFixed(2),
            range2b: c.time2b10pct_4yr && c.time2b90pct_4yr ? `${c.time2b10pct_4yr.toFixed(2)}-${c.time2b90pct_4yr.toFixed(2)}` : '--',
            throwaccperc: c.goodthrows2b_4yr && c.throws2bwloc_4yr ? percent(c.goodthrows2b_4yr / c.throws2bwloc_4yr) : '--',
            throw1stand3rd: `${c.firstandthirdthrows_4yr}/${c.firstandthirdopps_4yr} (${c.firstandthirdopps_4yr ? percent(c.firstandthirdthrows_4yr / c.firstandthirdopps_4yr) : '--'})`,
        };
        twoYearRows.push(catcherRowTwoYear);
    });

    leagueaverage.forEach((l) => {
        const lgAvgRowCurrentYear = {
            year: currentYear,
            name: `${l.abbr} ${translate('common.abbr-average')} ${currentYear}`,
            inn: '--',
            sb: l.sb_1yr && l.c_outs_1yr ? normalize(l.sb_1yr, l.c_outs_1yr, maxOuts1Yr).toFixed(0) : '--',
            cs: l.ccs_1yr && l.c_outs_1yr ? normalize(l.ccs_1yr, l.c_outs_1yr, maxOuts1Yr).toFixed(0) : '--',
            sbperc: percent(l.sb_1yr / (l.ccs_1yr + l.sb_1yr)),
            sbper100: l.sb_1yr && l.outs_1yr ? per100IP(l.sb_1yr, l.outs_1yr).toFixed(1) : '--',
            pb: l.pb_1yr && l.c_outs_1yr ? normalize(l.pb_1yr, l.c_outs_1yr, maxOuts1Yr).toFixed(0) : '--',
            sbperc2b: percent(l.sb2b_1yr / (l.sb2b_1yr + l.ccs2b_1yr)),
            sbperc3b: percent(l.sb3b_1yr / (l.sb3b_1yr + l.ccs3b_1yr)),
            pkoperatt: l.cpko_1yr && l.c_outs_1yr && l.cpkoatt_1yr && l.c_outs_1yr ? `${normalize(l.cpko_1yr, l.c_outs_1yr, maxOuts1Yr).toFixed(0)}/${normalize(l.cpkoatt_1yr, l.c_outs_1yr, maxOuts1Yr).toFixed(0)}` : '--',
            time2b: l.time2b_1yr?.toFixed(2),
            range2b: l.time2b10pct_1yr && l.time2b90pct_1yr ? `${l.time2b10pct_1yr.toFixed(2)}-${l.time2b90pct_1yr.toFixed(2)}` : '--',
            throwaccperc: l.goodthrows2b_1yr && l.throws2bwloc_1yr ? percent(l.goodthrows2b_1yr / l.throws2bwloc_1yr) : '--',
            throw1stand3rd: `${percent(l.firstandthirdthrows_1yr / l.firstandthirdopps_1yr)}`,
        };
        currentYearRows.push(lgAvgRowCurrentYear);

        const lgAvgRowTwoYear = {
            year: -1,
            name: `${l.abbr} ${translate('common.abbr-average')} ${currentYear - 1} - ${currentYear}`,
            inn: '--',
            sb: normalize(l.sb_4yr, l.c_outs_4yr, maxOuts4Yr),
            cs: normalize(l.ccs_4yr, l.c_outs_4yr, maxOuts4Yr),
            sbperc: percent(l.sb_4yr / (l.ccs_4yr + l.sb_4yr)),
            sbper100: l.sb_4yr && l.outs_4yr ? per100IP(l.sb_4yr, l.outs_4yr).toFixed(1) : '--',
            pb: normalize(l.pb_4yr, l.c_outs_4yr, maxOuts4Yr),
            sbperc2b: percent(l.sb2b_4yr / (l.sb2b_4yr + l.ccs2b_4yr)),
            sbperc3b: percent(l.sb3b_4yr / (l.sb3b_4yr + l.ccs3b_4yr)),
            pkoperatt: l.cpko_4yr && l.c_outs_4yr && l.cpkoatt_4yr && l.c_outs_4yr ? `${normalize(l.cpko_4yr, l.c_outs_4yr, maxOuts4Yr).toFixed(0)}/${normalize(l.cpkoatt_4yr, l.c_outs_4yr, maxOuts4Yr).toFixed(0)}` : '--',
            time2b: l.time2b_4yr?.toFixed(2),
            range2b: l.time2b10pct_4yr && l.time2b90pct_4yr ? `${l.time2b10pct_4yr.toFixed(2)}-${l.time2b90pct_4yr.toFixed(2)}` : '--',
            throwaccperc: l.goodthrows2b_4yr && l.throws2bwloc_4yr ? percent(l.goodthrows2b_4yr / l.throws2bwloc_4yr) : '--',
            throw1stand3rd: `${percent(l.firstandthirdthrows_4yr / l.firstandthirdopps_4yr)}`,
        };
        twoYearRows.push(lgAvgRowTwoYear);
    });
    return { currentYearRows, twoYearRows };
};

const catcherPickoffColumns = (translate) => [
    { id: 'name', title: translate('baseball.catcher_one'), type: colType.TEXT },
    { id: 'total', title: translate('sections.opponent.team.total'), type: colType.NUMBER },
    { id: 'ops', title: translate('sections.opponent.team.abbr-opportunities'), type: colType.NUMBER },
    { id: 'totalperops', title: `${translate('sections.opponent.team.total')} / ${translate('sections.opponent.team.abbr-opportunities')}`, type: colType.TEXT },
    { id: 'count00', title: '0-0', type: colType.TEXT },
    { id: 'count01', title: '0-1', type: colType.TEXT },
    { id: 'count02', title: '0-2', type: colType.TEXT },
    { id: 'count10', title: '1-0', type: colType.TEXT },
    { id: 'count11', title: '1-1', type: colType.TEXT },
    { id: 'count12', title: '1-2', type: colType.TEXT },
    { id: 'count20', title: '2-0', type: colType.TEXT },
    { id: 'count21', title: '2-1', type: colType.TEXT },
    { id: 'count22', title: '2-2', type: colType.TEXT },
    { id: 'count30', title: '3-0', type: colType.TEXT },
    { id: 'count31', title: '3-1', type: colType.TEXT },
    { id: 'count32', title: '3-2', type: colType.TEXT },
    { id: 'sb', title: translate('baseball.abbr-stolen-base'), type: colType.NUMBER },
    { id: 'ccs', title: translate('sections.opponent.team.abbr-ccs'), type: colType.NUMBER },
    { id: 'pko', title: translate('sections.opponent.team.abbr-pko'), type: colType.NUMBER },
    { id: 'sbperc', title: `${translate('baseball.abbr-stolen-base')}%`, type: colType.TEXT },
];

const formatCatcherPickoffRows = (translate, catcherpkof = [], teamcatcherpkof = [], mlbcatcherpkof = []) => {
    const rows = [];

    catcherpkof.forEach((r) => {
        const catcherRow = {
            name: `${r.usesname} ${r.lastname}`,
            total: r.total,
            ops: r.opps + r.total,
            totalperops: percent(r.total / (r.opps + r.total)),
            count00: r.po00,
            count01: r.po01,
            count02: r.po02,
            count10: r.po10,
            count11: r.po11,
            count12: r.po12,
            count20: r.po20,
            count21: r.po21,
            count22: r.po22,
            count30: r.po30,
            count31: r.po31,
            count32: r.po32,
            sb: r.sb,
            ccs: r.cs,
            pko: r.po,
            sbperc: percent(r.sb / (r.sb + r.cs)),
        };
        rows.push(catcherRow);
    });

    teamcatcherpkof.forEach((r) => {
        const teamRow = {
            name: translate('sections.opponent.team.team-total'),
            total: r.total,
            ops: r.opps + r.total,
            totalperops: percent(r.total / (r.opps + r.total)),
            count00: r.po00,
            count01: r.po01,
            count02: r.po02,
            count10: r.po10,
            count11: r.po11,
            count12: r.po12,
            count20: r.po20,
            count21: r.po21,
            count22: r.po22,
            count30: r.po30,
            count31: r.po31,
            count32: r.po32,
            sb: r.sb,
            ccs: r.cs,
            pko: r.po,
            sbperc: percent(r.sb / (r.sb + r.cs)),
        };
        rows.push(teamRow);
        const teamRowPerc = {
            name: translate('sections.opponent.team.team-pct-by-count'),
            total: '--',
            ops: '--',
            totalperops: '--',
            count00: percent(r.po00 / r.total),
            count01: percent(r.po01 / r.total),
            count02: percent(r.po02 / r.total),
            count10: percent(r.po10 / r.total),
            count11: percent(r.po11 / r.total),
            count12: percent(r.po12 / r.total),
            count20: percent(r.po20 / r.total),
            count21: percent(r.po21 / r.total),
            count22: percent(r.po22 / r.total),
            count30: percent(r.po30 / r.total),
            count31: percent(r.po31 / r.total),
            count32: percent(r.po32 / r.total),
            sb: '--',
            ccs: '--',
            pko: '--',
            sbperc: '--',
        };
        rows.push(teamRowPerc);
    });

    mlbcatcherpkof.forEach((r) => {
        const mlbAvgRow = {
            name: `MLB ${translate('sections.opponent.team.average')}`,
            total: r.total,
            ops: r.opps + r.total,
            totalperops: percent(r.total / (r.opps + r.total)),
            count00: percent(r.po00 / r.total),
            count01: percent(r.po01 / r.total),
            count02: percent(r.po02 / r.total),
            count10: percent(r.po10 / r.total),
            count11: percent(r.po11 / r.total),
            count12: percent(r.po12 / r.total),
            count20: percent(r.po20 / r.total),
            count21: percent(r.po21 / r.total),
            count22: percent(r.po22 / r.total),
            count30: percent(r.po30 / r.total),
            count31: percent(r.po31 / r.total),
            count32: percent(r.po32 / r.total),
            sb: r.sb,
            ccs: r.cs,
            pko: r.po,
            sbperc: percent(r.sb / (r.sb + r.cs)),
        };
        rows.push(mlbAvgRow);
    });

    return rows;
};

const superHeader = (year, span) => (
    <StyledSuperheaderRow>
        <td colSpan={span}>{year}</td>
    </StyledSuperheaderRow>
);

const StealingAgainstCatchers = ({ data }) => {
    const { t: translate } = useTranslation();
    const { catcher, leagueaverage, catcherpkof, teamcatcherpkof, mlbcatcherpkof } = data;
    const currentYear = new Date().getFullYear();

    const catcherRows = formatCatcherRows(translate, catcher, leagueaverage, currentYear);
    const catcherPickoffRows = formatCatcherPickoffRows(translate, catcherpkof, teamcatcherpkof, mlbcatcherpkof);
    return !!data && !!data.catcher && (
        <>
            <StyledDataTable
                title={translate('baseball.catcher_one')}
                superheader={superHeader(currentYear, 14)}
                columns={catcherColumns(translate)}
                rows={catcherRows.currentYearRows}
            />
            <StyledDataTable
                superheader={superHeader(`${currentYear - 1} - ${currentYear}`, 14)}
                columns={catcherColumns(translate)}
                rows={catcherRows.twoYearRows}
            />
            <br />
            <DataTable
                title={translate('sections.opponent.team.catcher-pickoffs')}
                columns={catcherPickoffColumns(translate)}
                rows={catcherPickoffRows}
            />
        </>
    );
};

StealingAgainstCatchers.propTypes = {
    data: PropTypes.shape({
        catcher: PropTypes.arrayOf(PropTypes.object),
        leagueaverage: PropTypes.arrayOf(PropTypes.object),
        catcherpkof: PropTypes.arrayOf(PropTypes.object),
        teamcatcherpkof: PropTypes.arrayOf(PropTypes.object),
        mlbcatcherpkof: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
};

export default StealingAgainstCatchers;
